import type React from "react";
import * as Sentry from "@sentry/react";
import { normalizePlanTier, tierIndex, type TierKey } from "@reach/core";
import type { WizardResult } from "../OnboardingWizard";
import { merchantFetch } from "./api";
import { PLAN_DETAILS } from "./planData";
import type { AnalyticsData, KbGap, KbItem, QueryLogItem, Settings } from "./types";

type AnyStateSetter<T> = React.Dispatch<React.SetStateAction<T>>;
type BillingInfo = {
  subscriptionStatus: string;
  planEffectiveUntil: string | null;
  trialEndsAt: string | null;
  billingProvider: string | null;
  compedReason: string | null;
};

type CreateSettingsHandlersParams = {
  analyticsPeriod: string;
  DEFAULT_SETTINGS: Settings;
  billingCycle: "monthly" | "annual";
  formState: Settings;
  isShopifyStore: boolean;
  promoCode: string;
  rememberProvider: boolean;
  savedFormState: React.MutableRefObject<Settings>;
  selectedTab: number;
  shopDomain: string;
  storeId: string | null;
  tabs: Array<{ id: string; content: string }>;

  setAnalyticsData: AnyStateSetter<AnalyticsData | null>;
  setAnalyticsLoading: AnyStateSetter<boolean>;
  setBillingInfo: AnyStateSetter<BillingInfo>;
  setConfigLoaded: AnyStateSetter<boolean>;
  setDirtySections: AnyStateSetter<Set<string>>;
  setFormState: AnyStateSetter<Settings>;
  setIsDirty: AnyStateSetter<boolean>;
  setIsModalOpen: AnyStateSetter<boolean>;
  setIsTrainingModalOpen: AnyStateSetter<boolean>;
  setKbDeleteLoading: AnyStateSetter<boolean>;
  setKbGaps: AnyStateSetter<KbGap[]>;
  setKbItems: AnyStateSetter<KbItem[]>;
  setLoading: AnyStateSetter<boolean>;
  setMonth: AnyStateSetter<{ month: number; year: number }>;
  setPendingDeleteId: AnyStateSetter<string | null>;
  setQContent: AnyStateSetter<string>;
  setQCustomReply: AnyStateSetter<string>;
  setQKeywords: AnyStateSetter<string>;
  setQQuestion: AnyStateSetter<string>;
  setQSource: AnyStateSetter<string>;
  setQSuggestions: AnyStateSetter<string>;
  setQTargetUrl: AnyStateSetter<string>;
  setQueryLog: AnyStateSetter<QueryLogItem[]>;
  setQueryLogLoading: AnyStateSetter<boolean>;
  setScrapeUrl: AnyStateSetter<string>;
  setDuplicateTopic: AnyStateSetter<KbItem | null>;
  setIsTrainingReadOnly: AnyStateSetter<boolean>;
  setSelectedKbId: AnyStateSetter<string | null>;
  setSelectedProvider: AnyStateSetter<"stripe" | "lemonsqueezy" | null>;
  setShowImportResultsModal: AnyStateSetter<boolean>;
  setShowAnalyticsWipeModal: AnyStateSetter<boolean>;
  setShowDeleteTopicModal: AnyStateSetter<boolean>;
  setShowOnboarding: AnyStateSetter<boolean>;
  setShowWipeModal: AnyStateSetter<boolean>;
  setStoreId: AnyStateSetter<string | null>;
  setToastActive: AnyStateSetter<boolean>;
  setToastMsg: AnyStateSetter<string>;
  setWipeConfirmText: AnyStateSetter<string>;
  setKbWipeConfirmText: AnyStateSetter<string>;
  setAnalyticsWipeLoading: AnyStateSetter<boolean>;
  setImportResults: AnyStateSetter<{
    inserted: string[];
    updated: string[];
    skipped: number;
    total: number;
  }>;
};

const ONBOARDING_METADATA_KEYS = new Set(["onboardingCompletedAt", "onboardingDismissedAt"]);

function hasMerchantSettings(settings: unknown): boolean {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) return false;
  return Object.keys(settings as Record<string, unknown>).some((key) => !ONBOARDING_METADATA_KEYS.has(key));
}

export function createSettingsHandlers({
  analyticsPeriod,
  DEFAULT_SETTINGS,
  billingCycle,
  formState,
  isShopifyStore,
  promoCode,
  rememberProvider,
  savedFormState,
  selectedTab,
  storeId,
  setAnalyticsData,
  setAnalyticsLoading,
  setBillingInfo,
  setConfigLoaded,
  setDirtySections,
  setFormState,
  setIsDirty,
  setIsModalOpen,
  setIsTrainingModalOpen,
  setKbDeleteLoading,
  setKbGaps,
  setKbItems,
  setLoading,
  setMonth,
  setPendingDeleteId,
  setQContent,
  setQCustomReply,
  setQKeywords,
  setQQuestion,
  setQSource,
  setQSuggestions,
  setQTargetUrl,
  setQueryLog,
  setQueryLogLoading,
  setScrapeUrl,
  setDuplicateTopic,
  setIsTrainingReadOnly,
  setSelectedKbId,
  setSelectedProvider,
  setShowImportResultsModal,
  setShowAnalyticsWipeModal,
  setShowDeleteTopicModal,
  setShowOnboarding,
  setShowWipeModal,
  setStoreId,
  setToastActive,
  setToastMsg,
  setWipeConfirmText,
  setKbWipeConfirmText,
  setAnalyticsWipeLoading,
  setImportResults,
  shopDomain,
  tabs,
}: CreateSettingsHandlersParams) {
  const toggleToast = () => setToastActive((active) => !active);
  const showToast = (msg: string) => {
    setToastMsg(msg);
    setToastActive(true);
  };
  const parseApiError = async (res: Response, fallback: string) => {
    const bodyText = await res.text().catch(() => "");
    if (!bodyText) return `${fallback} (${res.status})`;

    try {
      const parsed = JSON.parse(bodyText) as { error?: string; message?: string };
      const detail = parsed.error || parsed.message;
      return detail ? `${fallback} (${res.status}): ${detail}` : `${fallback} (${res.status})`;
    } catch {
      const compact = bodyText.replace(/\s+/g, " ").trim().slice(0, 180);
      return compact ? `${fallback} (${res.status}): ${compact}` : `${fallback} (${res.status})`;
    }
  };

  const closeTrainingModal = () => {
    setIsTrainingModalOpen(false);
    setQQuestion("");
    setQCustomReply("");
    setQSuggestions("");
    setQKeywords("");
    setQSource("manual");
    setQTargetUrl("");
    setQContent("");
    setDuplicateTopic(null);
    setIsTrainingReadOnly(false);
    setSelectedKbId(null);
  };

  const openNewTopicModal = () => {
    setQQuestion("");
    setQCustomReply("");
    setQSuggestions("");
    setQKeywords("");
    setQSource("manual");
    setQTargetUrl("");
    setQContent("");
    setDuplicateTopic(null);
    setIsTrainingReadOnly(false);
    setSelectedKbId(null);
    setIsTrainingModalOpen(true);
  };

  const handleMonthChange = (month: number, year: number) => setMonth({ month, year });

  const fetchConfig = async () => {
    try {
      const res = await merchantFetch(`/api/merchant/config?domain=${shopDomain}`);
      if (!res.ok) {
        throw new Error(await parseApiError(res, "Config load failed"));
      }

      const data = await res.json();
      if (data && !data.error) {
        setStoreId(typeof data.id === "string" ? data.id : null);
        const normalizedTier = normalizePlanTier(data.planTier || "free");
        const { settings, ...topLevelData } = data;
        const newSettings = {
          ...DEFAULT_SETTINGS,
          ...settings,
          ...topLevelData,
          planTier: normalizedTier,
          apps: Object.keys(DEFAULT_SETTINGS.apps).reduce(
            (acc, key) => ({
              ...acc,
              [key]: { ...DEFAULT_SETTINGS.apps[key], ...(settings?.apps?.[key] ?? {}) },
            }),
            {} as any
          ),
        };

        newSettings.desktopSideMargin =
          topLevelData.desktopSideMargin ??
          settings?.desktopSideMargin ??
          settings?.sideMargin ??
          newSettings.sideMargin ??
          DEFAULT_SETTINGS.desktopSideMargin;

        newSettings.desktopBottomMargin =
          topLevelData.desktopBottomMargin ??
          settings?.desktopBottomMargin ??
          settings?.bottomMargin ??
          newSettings.bottomMargin ??
          DEFAULT_SETTINGS.desktopBottomMargin;

        if (["agent", "rep", "vea"].includes(normalizedTier) && settings?.apps?.chatbot?.enabled === undefined) {
          newSettings.apps = { ...newSettings.apps, chatbot: { ...newSettings.apps.chatbot, enabled: true } };
        }

        const COLOR_MIGRATIONS: Record<string, { from: string; to: string }> = {
          wechat: { from: "#7BB32E", to: "#43DC71" },
          sms: { from: "#34C759", to: "#2563EB" },
          phone: { from: "#34C759", to: "#0EA5E9" },
        };

        Object.entries(COLOR_MIGRATIONS).forEach(([channel, { from, to }]) => {
          if (newSettings.apps[channel]?.color?.toUpperCase() === from.toUpperCase()) {
            newSettings.apps[channel] = { ...newSettings.apps[channel], color: to };
          }
        });

        setFormState(newSettings);
        setBillingInfo({
          subscriptionStatus: data.subscriptionStatus ?? "active",
          planEffectiveUntil: data.planEffectiveUntil ?? null,
          trialEndsAt: data.trialEndsAt ?? null,
          billingProvider: data.billingProvider ?? null,
          compedReason: data.compedReason ?? null,
        });

        if (data.billingProvider === "stripe" || data.billingProvider === "lemonsqueezy") {
          setSelectedProvider(data.billingProvider);
        }

        savedFormState.current = newSettings;

        const hasOnboardingMarker = !!(settings?.onboardingCompletedAt || settings?.onboardingDismissedAt);
        setShowOnboarding(!hasOnboardingMarker && !hasMerchantSettings(settings));

        setConfigLoaded(true);
        return newSettings;
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("fetchConfig failed", error);
    }
  };

  const fetchKb = async () => {
    try {
      const res = await merchantFetch(`/api/merchant/knowledge-base?domain=${shopDomain}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setKbItems(data);
      } else {
        Sentry.captureException(new Error("KB data is not an array"), { extra: { data } });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchGaps = async () => {
    try {
      const res = await merchantFetch(`/api/merchant/knowledge-gaps?domain=${shopDomain}`);
      if (res.status === 403) {
        setKbGaps([]);
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setKbGaps(
          data.map((gap: Record<string, unknown>, index: number) => ({
            ...gap,
            id: (typeof gap.id === "string" ? gap.id : null) || `${String(gap.queryText || gap.question || "gap")}-${index}`,
            question: typeof gap.question === "string" ? gap.question : typeof gap.queryText === "string" ? gap.queryText : "",
            hitCount: typeof gap.hitCount === "number" ? gap.hitCount : typeof gap.hits === "number" ? gap.hits : 1,
          }))
        );
      } else {
        Sentry.captureException(new Error("Gaps data is not an array"), { extra: { data } });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const effectivePeriod = tierIndex(formState.planTier || "free") === 0 ? "7" : analyticsPeriod;
      const res = await merchantFetch(`/api/merchant/analytics?domain=${shopDomain}&period=${effectivePeriod}&timezone=${encodeURIComponent(tz)}`);
      if (!res.ok) {
        setAnalyticsLoading(false);
        return;
      }
      const data = await res.json();
      if (!data.error) setAnalyticsData(data);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchQueryLog = async () => {
    const level = tierIndex(formState.planTier || "free");
    if (level < 2) return;
    setQueryLogLoading(true);
    try {
      const res = await merchantFetch(`/api/merchant/analytics/query-log?domain=${shopDomain}&period=${analyticsPeriod}`);
      const data = await res.json();
      if (Array.isArray(data)) setQueryLog(data);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setQueryLogLoading(false);
    }
  };

  const handleExportJSON = (filteredKbItems: any[]) => {
    const payload =
      filteredKbItems.length > 0
        ? filteredKbItems
        : [{
            title: "Example question?",
            reply: "Example reply.",
            keywords: "example, sample question, common typo",
            content: "",
            suggestions: "Option 1, Option 2",
            source: "manual",
            targetUrl: "",
          }];
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(payload, null, 2));
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "reach_brain_library.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleExportCSV = (filteredKbItems: any[]) => {
    const headers = ["Question", "Reply", "Keywords", "Suggestions", "Content", "Source", "Target URL", "Last Updated"];
    const rows = filteredKbItems.map((item) => [
      `"${(item.title || item.question || "").replace(/"/g, '""')}"`,
      `"${(item.reply || item.customReply || "").replace(/"/g, '""')}"`,
      `"${(item.keywords || "").replace(/"/g, '""')}"`,
      `"${(item.suggestions || "").replace(/"/g, '""')}"`,
      `"${(item.content || "").replace(/"/g, '""')}"`,
      `"${(item.source || "manual").replace(/"/g, '""')}"`,
      `"${(item.targetUrl || "").replace(/"/g, '""')}"`,
      filteredKbItems.length > 0 ? `"${new Date(item.updatedAt || item.createdAt).toLocaleDateString()}"` : '""',
    ]);
    const csvString = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const dataStr = "data:text/csv;charset=utf-8," + encodeURIComponent(csvString);
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "reach_brain_library.csv");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      try {
        const isJson = file.type === "application/json" || file.name.endsWith(".json");
        const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
        if (!isJson && !isCsv) {
          showToast("Unsupported file type. Please upload a .json or .csv file.");
          return;
        }

        let items: Partial<KbItem>[] = [];

        if (isJson) {
          let parsed: unknown;
          try { parsed = JSON.parse(content); } catch {
            showToast("Invalid JSON — the file could not be parsed. Check the format and try again.");
            return;
          }
          const arr = Array.isArray(parsed) ? parsed : [parsed];
          const normalized = arr.map((r: any) => ({
            title: r?.title || r?.question || "",
            reply: r?.reply || r?.customReply || r?.answer || "",
            keywords: r?.keywords || "",
            content: r?.content || "",
            suggestions: r?.suggestions || "",
            source: r?.source || (r?.sourceUrl === "reach:seed:greetings" ? "greeting" : "manual"),
            targetUrl: r?.targetUrl || (typeof r?.sourceUrl === "string" && /^https?:\/\//i.test(r.sourceUrl) ? r.sourceUrl : ""),
          }));
          const valid = normalized.filter((r: any) => r && (r.title || r.content));
          if (valid.length === 0) {
            showToast("No valid topics found in the JSON file. Each entry needs at least a title or content field.");
            return;
          }
          items = valid;
        } else {
          const lines = content.split("\n").filter((line) => line.trim());
          if (lines.length < 2) {
            showToast("The CSV file appears to be empty or missing a header row.");
            return;
          }
          const headers = lines[0].split(",").map((h) => h.trim().replace(/"/g, "").toLowerCase());
          const hasTitleCol = headers.some((h) => h === "title" || h === "question");
          const hasContentCol = headers.includes("content");
          if (!hasTitleCol && !hasContentCol) {
            showToast('Invalid CSV format — expected columns like title or question, reply, keywords, suggestions, content, source, and target URL. Download an export to see the correct format.');
            return;
          }
          items = lines.slice(1).map((line) => {
            const values = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((v) => v.trim().replace(/^"|"$/g, "").replace(/""/g, '"'));
            const item: Partial<KbItem> = {};
            headers.forEach((key, index) => {
              if (key === "question" || key === "title") item.title = values[index];
              else if (key === "reply" || key === "answer" || key === "customreply") item.reply = values[index];
              else if (key === "keywords" || key === "keyword") item.keywords = values[index];
              else if (key === "content") item.content = values[index];
              else if (key === "suggestions") item.suggestions = values[index];
              else if (key === "source") item.source = values[index];
              else if (key === "sourceurl" || key === "target url" || key === "targeturl") item.targetUrl = values[index];
            });
            return item;
          }).filter((item) => item.title || item.content);
          if (items.length === 0) {
            showToast("No valid rows found in the CSV. Make sure each row has at least a title or content value.");
            return;
          }
        }

        setLoading(true);
        const res = await merchantFetch(`/api/merchant/knowledge-base/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ domain: shopDomain, items }),
        });
        const data = await res.json();
        if (data.success) {
          setImportResults({
            inserted: Array.isArray(data.insertedTitles) ? data.insertedTitles : [],
            updated: Array.isArray(data.updatedTitles) ? data.updatedTitles : [],
            skipped: Number(data.skipped || 0),
            total: Number(data.count || items.length || 0),
          });
          setShowImportResultsModal(true);
          fetchKb();
        } else {
          showToast("Import failed. Please try again.");
        }
      } catch {
        showToast("Something went wrong reading the file. Make sure it's a valid .json or .csv.");
      }
      setLoading(false);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const GREETING_SEEDS = [
    // Openers
    { title: 'Hi',              reply: "Hi there! 👋 How can I help you today?",                                                                            keywords: "hi, hii, hey there, hello there",                              suggestions: "Browse products,View deals,Contact us,Track my order",     source: 'greeting' },
    { title: 'Hello',           reply: "Hello! 👋 What can I assist you with?",                                                                              keywords: "hello, hullo, helo, hello there",                              suggestions: "Browse products,View deals,Contact us,Track my order",     source: 'greeting' },
    { title: 'Hey',             reply: "Hey! Great to see you. How can I help?",                                                                              keywords: "hey, heyy, yo, hey there",                                     suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Good morning',    reply: "Good morning! ☀️ How can I help you start your day?",                                                                 keywords: "good morning, gud morning, morning, gm",                       suggestions: "Browse products,New arrivals,Contact us",                  source: 'greeting' },
    { title: 'Good afternoon',  reply: "Good afternoon! How can I assist you today?",                                                                         keywords: "good afternoon, afternoon, gud afternoon",                     suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Good evening',    reply: "Good evening! How can I help?",                                                                                       keywords: "good evening, evening, gud evening",                           suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    // Affirmations
    { title: 'Sure',            reply: "Of course! Where would you like to start?",                                                                           keywords: "sure, okay sure, yes sure",                                    suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Sounds good',     reply: "Great! What can I point you to?",                                                                                     keywords: "sounds good, sg, that sounds good",                            suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Cool',            reply: "Glad you think so! Anything I can help you find?",                                                                    keywords: "cool, nice, alright cool",                                     suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Awesome',         reply: "Right?! Is there anything I can help you with today?",                                                                keywords: "awesome, awsome, amazing, great",                              suggestions: "Browse products,New arrivals,View deals",                  source: 'greeting' },
    { title: 'Perfect',         reply: "Let's go! What can I help you with?",                                                                                 keywords: "perfect, perfct, exactly, great perfect",                      suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'OK',              reply: "Got it! Let me know if there's anything else I can help with.",                                                       keywords: "ok, okay, kk, k",                                              suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    // Gratitude
    { title: 'Thanks',          reply: "You're welcome! Anything else I can help with?",                                                                      keywords: "thanks, thx, ty, thankss",                                     suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Thank you',       reply: "Happy to help! Is there anything else on your mind?",                                                                 keywords: "thank you, thankyou, tysm, many thanks",                       suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    // Farewells
    { title: 'Bye',             reply: "Thanks for visiting! Come back anytime. 👋",                                                                          keywords: "bye, goodbye, cya, see ya",                                    suggestions: "Browse products,View deals",                               source: 'greeting' },
    { title: 'Never mind',      reply: "No worries at all! I'm still here if you need anything.",                                                             keywords: "never mind, nvm, doesnt matter, doesn't matter",               suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    // Humour & reactions
    { title: 'Lol',             reply: "😄 Happy to put a smile on your face! Can I help you find something?",                                               keywords: "lol, lool, haha, hahaha",                                      suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
    { title: 'Wow',             reply: "I know, right! Is there anything I can help you with today?",                                                         keywords: "wow, woow, omg, nice",                                         suggestions: "Browse products,New arrivals,Contact us",                  source: 'greeting' },
    // Help & confusion
    { title: 'Help',            reply: "I'm here to help! Tell me what you're looking for and I'll guide you.",                                               keywords: "help, pls help, please help, support",                         suggestions: "Browse products,Track my order,Contact us,Returns & refunds", source: 'greeting' },
    { title: 'I need help',     reply: "Of course! What do you need help with?",                                                                              keywords: "i need help, need help, can you help, help me",                suggestions: "Browse products,Track my order,Contact us,Returns & refunds", source: 'greeting' },
    { title: 'I am confused',   reply: "No worries — let's sort it out together. What's on your mind?",                                                      keywords: "i am confused, im confused, confused, not understanding",      suggestions: "Browse products,Track my order,Contact us",                source: 'greeting' },
    { title: 'Not sure',        reply: "That's totally fine! Here are a few popular areas to get you started.",                                               keywords: "not sure, unsure, don't know, dont know",                      suggestions: "Browse products,Track my order,Shipping info,Contact us",  source: 'greeting' },
    // Human escalation
    { title: 'Can I speak to someone', reply: "Absolutely! Here are the best ways to reach our team directly.",                                               keywords: "can i speak to someone, speak to someone, talk to support",    suggestions: "Contact us,Email us,WhatsApp us",                          source: 'greeting' },
    { title: 'Talk to a human', reply: "Of course! Our team is happy to help. Here's how to reach us.",                                                       keywords: "talk to a human, human agent, real person, speak to human",    suggestions: "Contact us,Email us,WhatsApp us",                          source: 'greeting' },
    // Bot identity
    { title: 'Who are you',     reply: "I'm your store assistant — here to help you find products, get answers, and connect you to the right team.",          keywords: "who are you, what are you, who is this",                       suggestions: "What can you help with?,Browse products,Contact us",       source: 'greeting' },
    { title: 'Are you a bot',   reply: "I'm the store assistant for this shop. Real people are also available if you need them!",                             keywords: "are you a bot, bot?, are you real, chatbot",                   suggestions: "What can you help with?,Contact a human,Browse products",  source: 'greeting' },
    { title: 'What can you do', reply: "I can answer questions about products, shipping, returns, and store policies — or connect you to the right team.",    keywords: "what can you do, what do you do, how can you help",            suggestions: "Browse products,Shipping info,Return policy,Contact us",   source: 'greeting' },
    // Discovery
    { title: 'What is new',     reply: "Great timing! Here are some things worth checking out.",                                                              keywords: "what is new, what's new, anything new, new stuff",             suggestions: "New arrivals,View deals,Browse products",                  source: 'greeting' },
    // Apology
    { title: 'Sorry',           reply: "No need to apologise at all! How can I help you today?",                                                              keywords: "sorry, sry, my bad, apologies",                                suggestions: "Browse products,View deals,Contact us",                    source: 'greeting' },
  ];

  const handleSeedGreetings = async () => {
    setLoading(true);
    try {
      const res = await merchantFetch(`/api/merchant/knowledge-base/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: shopDomain, items: GREETING_SEEDS }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Seeded ${data.inserted} new greetings${data.updated ? `, updated ${data.updated}` : ''}.`);
        fetchKb();
      } else {
        showToast('Seed failed.');
      }
    } catch {
      showToast('Error seeding greetings.');
    }
    setLoading(false);
  };

  const doWordPressCheckout = async (planId: TierKey, provider: "stripe" | "lemonsqueezy") => {
    if (!storeId) {
      showToast("Store is still loading. Please refresh and try again.");
      setLoading(false);
      return;
    }

    if (import.meta.env.DEV) {
      const res = await merchantFetch("/api/dev/set-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: shopDomain, tier: planId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showToast(`Switched to ${PLAN_DETAILS[planId]?.name || planId} (dev mode).`);
        await fetchConfig();
      } else {
        showToast(data.error || "Dev plan switch failed.");
      }
      setLoading(false);
      return;
    }

    const dashboardOrigin = window.location.origin;
    const successUrl = `${dashboardOrigin}${window.location.pathname}?shop=${shopDomain}&billing_success=1`;
    const cancelUrl = `${dashboardOrigin}${window.location.pathname}?shop=${shopDomain}&billing_cancelled=1`;

    if (rememberProvider) {
      await merchantFetch("/api/merchant/billing/provider", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId, provider }),
      }).catch(() => {});
    }

    const res = await merchantFetch("/api/merchant/billing/create-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        storeId,
        planTier: planId,
        billingCycle,
        provider,
        successUrl,
        cancelUrl,
        ...(promoCode.trim() ? { promoCode: promoCode.trim() } : {}),
      }),
    });
    const data = await res.json().catch(() => ({} as any));
    if (!res.ok || !data?.url) {
      showToast(data?.error || "Failed to start checkout. Please try again.");
      setLoading(false);
      return;
    }
    window.location.href = data.url;
  };

  const handleChange = (field: string, value: any) => {
    setFormState((prev) => {
      if (field === "planTier") {
        const nextTier = String(value || "free");
        const shouldEnableChatbotByDefault = ["agent", "rep", "vea"].includes(nextTier) && prev.apps?.chatbot?.enabled !== false;
        return {
          ...prev,
          [field]: value,
          ...(shouldEnableChatbotByDefault
            ? {
                apps: {
                  ...prev.apps,
                  chatbot: {
                    ...prev.apps.chatbot,
                    enabled: true,
                  },
                },
              }
            : {}),
        };
      }

      return { ...prev, [field]: value };
    });
    const id = tabs[selectedTab]?.id ?? "widget";
    setIsDirty(true);
    setDirtySections((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleDiscard = () => {
    setFormState(savedFormState.current);
    setIsDirty(false);
    setDirtySections(new Set());
  };

  const commitPlanChange = async (planId: TierKey) => {
    setLoading(true);
    try {
      if (import.meta.env.DEV && !isShopifyStore) {
        const res = await merchantFetch("/api/dev/set-plan", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ domain: shopDomain, tier: planId }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          showToast(planId === "free" ? "Subscription cancelled." : `Switched to ${PLAN_DETAILS[planId]?.name || planId} (dev mode).`);
          await fetchConfig();
        } else {
          showToast(data.error || "Dev plan switch failed.");
        }
        setLoading(false);
        return;
      }

      if (isShopifyStore && planId === "free") {
        showToast("To cancel, go to your Shopify Admin → Apps → Reach and remove the subscription there.");
        setLoading(false);
        setIsModalOpen(false);
        return;
      }

      if (isShopifyStore && planId !== "free") {
        if (!storeId) {
          showToast("Store is still loading. Please refresh and try again.");
          return;
        }
        const res = await merchantFetch("/api/merchant/billing/shopify/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId, tier: planId, billingCycle }),
        });
        const data = await res.json().catch(() => ({} as any));
        if (!res.ok || !data?.confirmationUrl) {
          showToast(data?.error || "Failed to start Shopify subscription.");
          return;
        }
        window.location.href = data.confirmationUrl;
        return;
      }

      if (!isShopifyStore && planId === "free") {
        if (!storeId) {
          showToast("Store still loading.");
          setLoading(false);
          return;
        }
        const res = await merchantFetch("/api/merchant/billing/cancel", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ storeId }),
        });
        const data = await res.json().catch(() => ({} as any));
        if (res.ok) {
          setBillingInfo((prev) => ({
            ...prev,
            subscriptionStatus: "cancelled",
            planEffectiveUntil: data?.effectiveDate ?? prev.planEffectiveUntil,
          }));
          showToast("Subscription cancelled. You retain access until the end of your billing period.");
          await fetchConfig();
        } else {
          showToast(data?.error || "Failed to cancel subscription.");
        }
        setLoading(false);
        setIsModalOpen(false);
        return;
      }

      if (!isShopifyStore) {
        showToast("An error occurred - please use the payment selector.");
        setLoading(false);
        return;
      }
    } catch {
      showToast("Network error updating subscription.");
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleOnboardingComplete = async (result: WizardResult) => {
    const mergedState: Settings = {
      ...formState,
      ...(result.timezone !== undefined ? { timezone: result.timezone } : {}),
      ...(result.startHour !== undefined ? { startHour: result.startHour } : {}),
      ...(result.endHour !== undefined ? { endHour: result.endHour } : {}),
      ...(result.botName !== undefined ? { botName: result.botName } : {}),
      ...(result.welcomeMsg !== undefined ? { welcomeMsg: result.welcomeMsg } : {}),
      onboardingCompletedAt: new Date().toISOString(),
      ...(result.anchorColor !== undefined
        ? {
            anchorColor: result.anchorColor,
            botColor: result.anchorColor,
            supportAppsButtonColor: result.anchorColor,
            supportAppsButtonWrapperBg: result.anchorColor,
            chatbotButtonWrapperBg: result.anchorColor,
            dockTintColor: result.anchorColor,
          }
        : {}),
      apps: {
        ...formState.apps,
        [result.channelId]: {
          ...formState.apps[result.channelId],
          enabled: true,
          value: result.channelValue,
        },
        chatbot: {
          ...formState.apps.chatbot,
          ...(result.botName !== undefined ? { enabled: true } : {}),
        },
      },
    };
    setFormState(mergedState);
    setLoading(true);
    try {
      const res = await merchantFetch("/api/merchant/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: shopDomain, config: mergedState }),
      });
      if (!res.ok) {
        showToast(await parseApiError(res, "Setup save failed"));
      } else {
        const data = await res.json().catch(() => null);
        if (data?.success) showToast("Setup complete! Settings saved.");
        else showToast("Setup complete!");
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("handleOnboardingComplete save failed", error);
      showToast("Setup save failed. Check the browser console for details.");
    }
    setLoading(false);
    localStorage.setItem(`reach_onboarded_${shopDomain}`, "1");
    setShowOnboarding(false);
  };

  const handleOnboardingDismiss = async () => {
    const dismissedState: Settings = {
      ...formState,
      onboardingDismissedAt: new Date().toISOString(),
    };
    setFormState(dismissedState);
    savedFormState.current = dismissedState;
    localStorage.setItem(`reach_onboarded_${shopDomain}`, "1");
    setShowOnboarding(false);
    try {
      const res = await merchantFetch(`/api/merchant/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: shopDomain, config: dismissedState }),
      });
      if (!res.ok) {
        Sentry.captureMessage("Onboarding dismiss marker save failed", {
          level: "warning",
          extra: { status: res.status, shopDomain },
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await merchantFetch(`/api/merchant/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: shopDomain, config: formState }),
      });
      if (!res.ok) {
        showToast(await parseApiError(res, "Save failed"));
      } else {
        const data = await res.json().catch(() => null);
        if (data?.success) {
          showToast("Settings saved successfully!");
          await fetchConfig();
          setIsDirty(false);
          setDirtySections(new Set());
        } else {
          showToast("Save failed.");
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("handleSave failed", error);
      showToast("Save failed. Check the browser console for details.");
    }
    setLoading(false);
  };

  const handleDeleteKbItem = (id: string) => {
    setPendingDeleteId(id);
    setShowDeleteTopicModal(true);
  };

  const handleWipeKb = async () => {
    setKbDeleteLoading(true);
    try {
      const res = await merchantFetch(`/api/merchant/knowledge-base?domain=${shopDomain}`, { method: "DELETE" });
      if (res.ok) {
        showToast("Brain library wiped clean.");
        fetchKb();
        setShowWipeModal(false);
        setKbWipeConfirmText("");
      } else {
        showToast("Failed to wipe library.");
      }
    } catch {
      showToast("Error wiping library.");
    }
    setKbDeleteLoading(false);
  };

  const handleWipeAnalytics = async () => {
    setAnalyticsWipeLoading(true);
    try {
      const res = await merchantFetch(`/api/merchant/analytics?domain=${shopDomain}`, { method: "DELETE" });
      if (res.ok) {
        showToast("Analytics data permanently deleted.");
        setAnalyticsData(null);
        setShowAnalyticsWipeModal(false);
        setWipeConfirmText("");
      } else {
        showToast("Failed to delete analytics data.");
      }
    } catch {
      showToast("Error deleting analytics data.");
    }
    setAnalyticsWipeLoading(false);
  };

  const normalizeScrapeUrl = (value: string) => {
    if (!value.toLowerCase().includes("https")) {
      setScrapeUrl(`https://${value.trim()}`);
    }
  };

  return {
    closeTrainingModal,
    commitPlanChange,
    doWordPressCheckout,
    fetchAnalytics,
    fetchConfig,
    fetchGaps,
    fetchKb,
    fetchQueryLog,
    handleChange,
    handleDeleteKbItem,
    handleDiscard,
    handleExportCSV,
    handleExportJSON,
    handleImportFile,
    handleSeedGreetings,
    handleMonthChange,
    handleOnboardingComplete,
    handleOnboardingDismiss,
    handleSave,
    handleWipeAnalytics,
    handleWipeKb,
    normalizeScrapeUrl,
    openNewTopicModal,
    showToast,
    toggleToast,
  };
}
